/**
 * Mini-app icon — the PURE half: the spec's shape, the tile geometry every
 * renderer derives from, the text-fitting model, the default tile, the colour
 * and hash helpers.
 *
 * Split out of `miniapp-icon.ts` (#788) for ONE reason: this half must be
 * importable with **no zod**. The PDApp SDK renders another app's icon inside
 * an app page, and its bundle is ~18 KB gzipped with one workspace dependency
 * — pulling zod and every schema in `@papan/types` to read a tile's geometry
 * is not a trade worth making. The schema half (`miniapp-icon.ts`) imports
 * from here and re-exports everything, so every existing caller is unchanged.
 *
 * Nothing here touches the DOM, the filesystem or the network: it runs the
 * same in the browser renderer, the server PNG renderer and the SDK.
 */
import { stripMiniappFolderExt } from './file-extensions.js'

/**
 * The icon spec, structurally. The zod schema in `miniapp-icon.ts` is asserted
 * against this type, so the two can never drift: change one and the other
 * fails to compile.
 */
export interface MiniappIconSpec {
  v: 1
  /** Foreground text — at most one of `text` / `icon` / `image`. */
  text?: string
  /** A Lucide icon name, or the reserved `pdapp` mark. */
  icon?: string
  /** An uploaded foreground image, stored as a file beside the manifest. */
  image?: true
  bg:
    | { type: 'solid'; color: string }
    | { type: 'gradient'; from: string; to: string; toStop?: number }
    | { type: 'image' }
  emojiStyle: 'color' | 'mono'
  textColor?: string
  shape?: MiniappTileShape
}

// ---------------------------------------------------------------------------
// Grapheme counting
// ---------------------------------------------------------------------------
//
// An emoji — including ZWJ sequences (👨‍👩‍👧), flags, and skin-tone modifiers —
// must count as ONE user-perceived character. `Intl.Segmenter` is the correct
// primitive and is available in all target browsers and Node ≥ 20. We reach
// for it through a structural cast so this module needn't depend on the
// `Intl.Segmenter` lib types (shared/types compiles with `lib: ["ES2022"]`,
// no DOM). Falls back to code-point iteration when Segmenter is unavailable.

interface SegmenterLike {
  segment(input: string): Iterable<{ segment: string }>
}
type SegmenterCtor = new (
  locales?: string,
  options?: { granularity?: 'grapheme' | 'word' | 'sentence' },
) => SegmenterLike

const SegmenterImpl: SegmenterCtor | undefined =
  typeof Intl !== 'undefined'
    ? (Intl as unknown as { Segmenter?: SegmenterCtor }).Segmenter
    : undefined

/** Split a string into its grapheme clusters (Segmenter, code-point fallback). */
function graphemes(text: string): string[] {
  if (SegmenterImpl) {
    const out: string[] = []
    for (const s of new SegmenterImpl('en', { granularity: 'grapheme' }).segment(text)) {
      out.push(s.segment)
    }
    return out
  }
  // Fallback: code-point iteration. Undercounts multi-code-point ZWJ/flag
  // sequences, but keeps the module functional where Segmenter is absent.
  return [...text]
}

/** `graphemes`, for renderers that must walk text the way the layout counts it
 *  (the server's outline renderer, #1015). */
export { graphemes as splitGraphemes }

/** Number of user-perceived characters (grapheme clusters) in `text`. */
export function graphemeCount(text: string): number {
  return graphemes(text).length
}

// ---------------------------------------------------------------------------
// The spec schema

/** Max user-perceived characters (grapheme clusters) allowed in icon text. */
export const MINIAPP_ICON_TEXT_MAX_GRAPHEMES = 30

/**
 * What an agent must know to write an icon spec — appended to the icon tools' `get` (the in-app `pdapp_icon` and the
 * MCP's `papan_icon`) so a model self-corrects before a `set`. ONE text for both tools (#1523); it describes what an
 * agent can SET (an uploaded-image icon is set in the app's settings, never by a tool).
 */
export const MINIAPP_ICON_CONSTRAINTS =
  'Constraints: the foreground is EITHER `text` OR `icon`, never both. ' +
  `text is 1–${MINIAPP_ICON_TEXT_MAX_GRAPHEMES} characters (grapheme clusters; an emoji counts as 1) and is wrapped + auto-scaled to fit the squircle. ` +
  'icon is a Lucide icon name in kebab-case (e.g. "layout-grid", "rocket"); when set, text must be omitted. ' +
  'bg is either {type:"solid",color} or {type:"gradient",from,to} (a 2-color VERTICAL top→bottom linear gradient); ' +
  'all colors are #rrggbb hex; emojiStyle is "color" (Noto Color Emoji) or "mono" (outline, follows textColor); ' +
  'omit textColor for auto-contrast (white/near-black by background luminance) — it also colors a Lucide icon.'

/** The schema's OTHER text limit: UTF-16 code units, which is what a zod
 *  `.max()` counts. Both are enforced — graphemes bound what a person sees, units
 *  bound what is stored — and anything restating them (the SDK's zod-free
 *  validator) must restate BOTH, from here. */
export const MINIAPP_ICON_TEXT_MAX_UNITS = 240
// ---------------------------------------------------------------------------
// The PDApp mark — the ONE non-Lucide glyph the icon system knows
// ---------------------------------------------------------------------------
//
// A spec's `icon` is otherwise a Lucide name resolved through
// `lucide-react/dynamicIconImports`. This is a RESERVED name that resolves to
// our own geometry instead: it is seeded into every renderer's node cache, so
// the lookup short-circuits before any dynamic import and no Lucide icon can
// ever shadow it. `isKnownLucideIcon` allowlists it so a write isn't rejected.
//
// It is drawn as FOUR SEPARATE SEGMENTS with FLAT caps and miter joins — the
// SVG defaults, NOT Lucide's round house style — because the corner spur where
// a diagonal's flat end runs past a bar's IS the mark. Rounding the caps or
// closing the path into one subpath erases it. Every renderer therefore has to
// ask `glyphStrokeStyle` rather than hardcoding Lucide's round pair.

/** Reserved `icon` name for the PDApp mark. Matches `LUCIDE_ICON_RE` so it
 *  round-trips through the schema like any other name. */
export const PDAPP_GLYPH_NAME = 'pdapp'

/** The mark on Lucide's 24-unit grid, so it scales with every other glyph.
 *  Corners at (5.5,3) (18.5,3) (5.5,21) (18.5,21) — 13 x 20 including the
 *  stroke, which preserves the source artwork's 1:1.4 proportion to within
 *  1.3%. Narrower than most Lucide glyphs; that is the shape, not an error. */
export const PDAPP_GLYPH_PATH = 'M5.5 3h13M5.5 21h13M5.5 21 18.5 3M18.5 21 5.5 3'

/**
 * What uploaded icon art may weigh when it travels INSIDE portable metadata
 * (#788) — the budget, and the geometry that meets it.
 *
 * This is NOT the icon's render size. Portable metadata is pasted into a page's
 * SOURCE: it rides a collaborative Y.Doc edit and then ships, base64'd, to every
 * visitor of the author's app, while the tile it draws is tens of pixels across.
 * So both producers downscale far below the drive's own render sizes, and neither
 * emits an asset over the ceiling — the server export drops it, the browser's
 * produce mode refuses it (it has an author in front of it who can pick another
 * picture).
 *
 * Here rather than beside either producer because they must not drift: one of
 * them is `features/files/core` (node, sharp) and the other is a browser canvas.
 */
export const PDAPP_PORTABLE_BG_W = 320
export const PDAPP_PORTABLE_FG_DIM = 256
export const PDAPP_PORTABLE_ASSET_MAX_BYTES = 96 * 1024

/** Take the first `max` grapheme clusters of `t` (Segmenter, code-point fallback). */
export function takeGraphemes(t: string, max: number): string {
  const Seg = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity?: string }) => { segment(s: string): Iterable<{ segment: string }> } }).Segmenter
  if (!Seg) return [...t].slice(0, max).join('')
  let out = ''
  let n = 0
  for (const s of new Seg('en', { granularity: 'grapheme' }).segment(t)) {
    if (n >= max) break
    out += s.segment
    n += 1
  }
  return out
}

/** A UTF-16 cut can land mid-surrogate-pair; a lone high surrogate is not text. */
function isWellFormedText(s: string): boolean {
  const last = s.charCodeAt(s.length - 1)
  return !(last >= 0xd800 && last <= 0xdbff)
}

/**
 * Clamp icon text to the spec's bounds — BOTH of them: at most
 * `MINIAPP_ICON_TEXT_MAX_GRAPHEMES` clusters AND at most
 * `MINIAPP_ICON_TEXT_MAX_UNITS` UTF-16 units.
 *
 * Here rather than in a component because every surface that offers a foreground
 * has to agree with the schema that will reject it: the full icon maker, the
 * compact creator in the chat, and anything added later. Counting code POINTS is
 * neither bound, and it let 240 astral characters through once.
 */
export function clampIconText(t: string): string {
  let s = takeGraphemes(t, MINIAPP_ICON_TEXT_MAX_GRAPHEMES)
  if (s.length > MINIAPP_ICON_TEXT_MAX_UNITS) {
    s = s.slice(0, MINIAPP_ICON_TEXT_MAX_UNITS)
    while (s.length > 0 && !isWellFormedText(s)) s = takeGraphemes(s, graphemeCount(s) - 1)
  }
  return s
}

/**
 * Ready-made icon backgrounds (top → bottom gradients).
 *
 * Shared DATA, not a component: the full icon maker offers them as swatches in a
 * 900px dialog and the compact creator offers the same set in a chat column, and
 * two lists of "the tasteful ones" would drift into two different products.
 */
export const MINIAPP_ICON_BG_PRESETS: ReadonlyArray<{ from: string; to: string }> = [
  // Deep, low-contrast ramps — the PDApp card collection's own palette (beside the default
  // maroon #800000 → #78095f, which the editor already offers from the app's default spec).
  { from: '#1F5C45', to: '#174634' }, // forest
  { from: '#B26B00', to: '#8A5200' }, // amber
  { from: '#1E3A5F', to: '#15294A' }, // navy
  { from: '#5A2A6E', to: '#3E1C4D' }, // plum
  { from: '#1F5A63', to: '#143F46' }, // teal
  { from: '#2B2B2B', to: '#1A1A1A' }, // graphite
  { from: '#FAF9F6', to: '#DEDBD4' }, // paper — white to warm grey (a dark glyph: see applyPreset)
]

/** Renderer-agnostic node, same `[tag, attrs][]` shape as Lucide's `__iconNode`. */
export const PDAPP_GLYPH_NODE: Array<[string, Record<string, string | number>]> = [
  ['path', { d: PDAPP_GLYPH_PATH }],
]

/** Stroke style for a glyph by name. Lucide's house style is round/round; the
 *  PDApp mark is the one exception and must stay flat — see above. */
export function glyphStrokeStyle(name: string): {
  cap: 'butt' | 'round'
  join: 'miter' | 'round'
} {
  return name === PDAPP_GLYPH_NAME
    ? { cap: 'butt', join: 'miter' }
    : { cap: 'round', join: 'round' }
}

/** Glyph rendered when a mini-app has no custom icon (the default tile).
 *  NOT a Lucide name since 2026-08-14 — it is the reserved PDApp mark. */
export const MINIAPP_ICON_DEFAULT_LUCIDE = PDAPP_GLYPH_NAME

/** The `bg` background: a solid fill, a 2-stop vertical gradient, or a custom
 *  uploaded image. Named so the field-wise self-heal (`coerceMiniappIconSpec`) can
 *  validate a `bg` in isolation.
 *
 *  `image` carries NO path — the asset lives at a canonical dot-prefixed file at
 *  the `.miniapp` folder root (see features/files/core/icon-assets.ts), so there's
 *  zero path-traversal surface and the spec stays tiny (well under the 64 KB manifest
 *  cap). Image backgrounds are drawn by the server-composited PNG path, never
// the shrink and this has to go back to 2 MB. If you tighten it further, measure the
// SHRUNK output: a 512² true-colour PNG foreground is still ~340 KB.
//
// SVG stays far tighter: it's the one input that reaches an XML parser + librsvg
// (hardened, but still the sharp edge), it CANNOT be shrunk client-side (it's
// vector), and 256 KB is already a large icon.

/** The square each slot is rendered down to at upload (icon-assets.ts). Shared so
 *  the CLIENT can pre-shrink a pick to the same target before sending it — the
 *  server still re-encodes, so this is a bandwidth/latency optimisation, never a
 *  trust boundary. */
export const MINIAPP_ICON_BG_RENDER_DIM = 1024
export const MINIAPP_ICON_FG_RENDER_DIM = 512

/** Max upload bytes for a RASTER icon asset (background PNG/JPEG, foreground PNG/JPEG). */
export const MINIAPP_ICON_RASTER_MAX_BYTES = 1024 * 1024
/** Max upload bytes for an SVG foreground — deliberately tighter than raster. */
export const MINIAPP_ICON_SVG_MAX_BYTES = 256 * 1024
/** Ceiling for the upload route's blanket pre-buffer guard = the largest slot limit. */
export const MINIAPP_ICON_ASSET_MAX_BYTES = MINIAPP_ICON_RASTER_MAX_BYTES

/** An upload limit in bytes → the short label used to name it in the editor hint,
 *  the editor's rejection message, and the server's thrown errors ("1 MB",
 *  "256 KB"). One helper so those three can't describe the same cap differently. */
export function formatIconAssetLimit(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${bytes / (1024 * 1024)} MB` : `${bytes / 1024} KB`
}

/** `accept` attribute for the background file input. */
export const MINIAPP_ICON_BG_ACCEPT = 'image/png,image/jpeg'
/** `accept` attribute for the foreground file input. */
export const MINIAPP_ICON_FG_ACCEPT = 'image/png,image/jpeg,image/svg+xml'

/** Why a picked file can't be used. `size` carries the limit that applies to THAT
 *  file's format, so the message can name the real number (SVG ≠ raster). */
export type IconAssetPickError =
  | { code: 'type' }
  | { code: 'size'; format: 'svg' | 'raster'; maxBytes: number }

/** Format of a picked file, from its MIME type (extension only when the browser
 *  reports none — some pickers do). `null` = not an accepted format. */
function pickedAssetFormat(name: string, type: string): IconAssetFormatHint | null {
  const t = type.trim().toLowerCase()
  if (t === 'image/svg+xml') return 'svg'
  if (t === 'image/png') return 'png'
  if (t === 'image/jpeg' || t === 'image/jpg') return 'jpeg'
  // A present-but-unaccepted type (image/gif, application/pdf, …) is a rejection —
  // don't let a misleading extension talk us past it.
  if (t) return null
  const ext = /\.([a-z0-9]+)$/.exec(name.toLowerCase())?.[1]
  if (ext === 'svg') return 'svg'
  if (ext === 'png') return 'png'
  if (ext === 'jpg' || ext === 'jpeg') return 'jpeg'
  return null
}

type IconAssetFormatHint = 'png' | 'jpeg' | 'svg'

/**
 * Validate a file the user just picked in the editor, BEFORE staging or
 * uploading it. Returns `null` when it's usable, else the reason.
 *
 * This is a fast client-side gate for feedback only — it trusts the browser's
 * reported MIME/size and is NOT a security boundary. The server re-sniffs the
 * real format from the bytes and re-checks the size (never trusting either), so
 * this can only ever be *stricter feedback*, never the thing that keeps a bad
 * file out. Kept pure (plain fields, not `File`) so `shared/types` stays DOM-free.
 */
export function validateIconAssetPick(
  kind: 'bg' | 'fg',
  file: { name: string; type: string; size: number },
): IconAssetPickError | null {
  const format = pickedAssetFormat(file.name, file.type)
  if (format === null) return { code: 'type' }
  // An SVG background would have to be flattened to an opaque JPEG anyway — the
  // bg slot takes raster only.
  if (format === 'svg' && kind === 'bg') return { code: 'type' }
  const maxBytes = format === 'svg' ? MINIAPP_ICON_SVG_MAX_BYTES : MINIAPP_ICON_RASTER_MAX_BYTES
  if (file.size > maxBytes) {
    return { code: 'size', format: format === 'svg' ? 'svg' : 'raster', maxBytes }
  }
  return null
}

/** Read the derived asset cache-bust rev out of `metadata_json` (or `null`). */
//
// The tile is PORTRAIT by default; `shape: 'square'` restores the
// original square squircle. EVERY renderer (the React component, the SVG-string
// renderer, and the server `sharp` render) must derive its viewBox, clip path,
// glyph box, text box and image box from `tileGeometry` — a hardcoded 512 in any
// of them silently desyncs that surface from the other two, which is precisely
// the class of bug the shared squircle constant was introduced to prevent.

/** Portrait tile. Height is pinned to the original 512 grid so `size` (= the
 *  tile's height) keeps meaning the same thing everywhere; the WIDTH is the
 *  tuning knob for how elongated the tile reads. Currently 444:512 ≈ 1:1.15.
 *  Everything downstream — the clip path, the glyph/text/image boxes, the
 *  rendered pixel size, and the background cover-crop — is DERIVED from these
 *  two, so changing the width here is the whole edit. */
export const MINIAPP_TILE_PORTRAIT_H = 512
export const MINIAPP_TILE_PORTRAIT_W = 444
/** Square tile (the `shape: 'square'` escape hatch) — the original grid. */
export const MINIAPP_TILE_SQUARE_DIM = 512

/** Background cover-crop WIDTH, DERIVED from the tile aspect — cropping square
 *  and then drawing the result with `xMidYMid slice` into the narrower portrait
 *  box would throw the sides away a second time. It was briefly a hand-computed
 *  literal, which went stale the moment the tile width was tuned; deriving it
 *  means the crop can never disagree with the tile it is cropped for.
 *
 *  Assets uploaded before 2026-08-14 are square and still render (slice crops
 *  them); a re-upload recovers the full frame. A `shape: 'square'` app
 *  letterboxes a hair at the sides instead — the safer direction for an escape
 *  hatch. */
export const MINIAPP_ICON_BG_RENDER_W = Math.round(
  (MINIAPP_ICON_BG_RENDER_DIM * MINIAPP_TILE_PORTRAIT_W) / MINIAPP_TILE_PORTRAIT_H,
)

/** Continuous-corner control-point factor. 0.727 of the radius along each axis —
 *  squarer than a circle's 0.5523, matching the iOS-style squircle the square
 *  tile has always used. */
const CORNER_K = 0.727

/**
 * Rounded-rect path with iOS-style continuous corners, in grid units.
 *
 * At (512, 512, 256) this reproduces `MINIAPP_SQUIRCLE_PATH` to within ~0.1
 * units — the square literal is nonetheless KEPT verbatim rather than generated,
 * because it is pinned by tests and shipped in stored favicons, and regenerating
 * it would churn every cached PNG for a curve nobody asked to change.
 */
function roundedSuperellipsePath(w: number, h: number, r: number): string {
  const n = (v: number) => Number(v.toFixed(3))
  const c = r * CORNER_K
  return (
    `M${n(r)},0` +
    `L${n(w - r)},0C${n(w - r + c)},0 ${n(w)},${n(r - c)} ${n(w)},${n(r)}` +
    `L${n(w)},${n(h - r)}C${n(w)},${n(h - r + c)} ${n(w - r + c)},${n(h)} ${n(w - r)},${n(h)}` +
    `L${n(r)},${n(h)}C${n(r - c)},${n(h)} 0,${n(h - r + c)} 0,${n(h - r)}` +
    `L0,${n(r)}C0,${n(r - c)} ${n(r - c)},0 ${n(r)},0Z`
  )
}

/** Portrait corner radius — 22% of the WIDTH, so the corner reads at the same
 *  visual weight as the square tile's despite the narrower box. */
const PORTRAIT_RADIUS = 80

/** Clip path for the portrait tile, in `MINIAPP_TILE_PORTRAIT_W/H` units. */
export const MINIAPP_PORTRAIT_PATH = roundedSuperellipsePath(
  MINIAPP_TILE_PORTRAIT_W,
  MINIAPP_TILE_PORTRAIT_H,
  PORTRAIT_RADIUS,
)

/** The two tile shapes. ABSENT on a spec means `portrait` — every stored spec
 *  predates the field, so absence has to resolve to the current default or the
 *  change would reach nobody. */
export type MiniappTileShape = 'portrait' | 'square'

export interface MiniappTileGeometry {
  shape: MiniappTileShape
  /** viewBox width / height in grid units. */
  w: number
  h: number
  /** Clip path for this shape, in the same grid units. */
  clipPath: string
  /** Lucide/PDApp glyph box: side length + top-left offset. */
  glyph: { dim: number; x: number; y: number }
  /** Centred safe box for auto-fitted text. */
  text: { w: number; h: number }
  /** Centred box for a composited image foreground (emoji or uploaded asset). */
  image: { dim: number; x: number; y: number }
}

/** Resolve a spec's `shape` (absent ⇒ portrait) to its drawing geometry. */
export function tileGeometry(shape: MiniappTileShape | undefined): MiniappTileGeometry {
  if (shape === 'square') {
    const d = MINIAPP_TILE_SQUARE_DIM
    const glyphDim = 300 // ~59% of the square grid — the historical value
    const imageDim = d * 0.62
    return {
      shape: 'square',
      w: d,
      h: d,
      clipPath: MINIAPP_SQUIRCLE_PATH,
      glyph: { dim: glyphDim, x: (d - glyphDim) / 2, y: (d - glyphDim) / 2 },
      text: { w: 300, h: 340 },
      image: { dim: imageDim, x: (d - imageDim) / 2, y: (d - imageDim) / 2 },
    }
  }
  const w = MINIAPP_TILE_PORTRAIT_W
  const h = MINIAPP_TILE_PORTRAIT_H
  // The glyph is sized off the NARROW axis — scaling it off the height would
  // overflow the sides. 59% of the width matches the square tile's ratio of
  // glyph to the dimension that constrains it.
  const glyphDim = Math.round(w * 0.59)
  const imageDim = Math.round(w * 0.62)
  return {
    shape: 'portrait',
    w,
    h,
    clipPath: MINIAPP_PORTRAIT_PATH,
    glyph: { dim: glyphDim, x: (w - glyphDim) / 2, y: (h - glyphDim) / 2 },
    // Width follows the narrower box; height keeps the square tile's generous
    // vertical budget so stacked lines still have room.
    text: { w: Math.round(w * 0.82), h: 340 },
    image: { dim: imageDim, x: (w - imageDim) / 2, y: (h - imageDim) / 2 },
  }
}

/** Rendered pixel size of a tile whose HEIGHT is `size`. `size` means height at
 *  every call site — the tallest dimension is what sets a row's rhythm, so
 *  keeping it fixed means a portrait tile narrows rather than pushing layout. */
export function tilePixelSize(
  shape: MiniappTileShape | undefined,
  size: number,
): { w: number; h: number } {
  const g = tileGeometry(shape)
  return { w: Math.round((size * g.w) / g.h), h: size }
}

/** SVG path (`d` attribute) for the icon squircle, in 512-grid units. */
export const MINIAPP_SQUIRCLE_PATH =
  'M512,256C512,442.023 442.042,512 256.018,512C69.995,512 0,442.045 0,256.022C0,69.998 69.953,0 255.976,0C441.999,0 512,69.977 512,256Z'

/**
 * CSS `clip-path: path(...)` form of the squircle. NOTE: the path is in
 * 512-grid units and does NOT scale with the clipped element, so this is only
 * pixel-correct on a 512×512 box. `MiniappIcon` renders through an SVG
 * `viewBox` instead to scale losslessly; keep this for the rare 512-box case.
 */
export const MINIAPP_SQUIRCLE_CLIP_PATH = `path('${MINIAPP_SQUIRCLE_PATH}')`

// ---------------------------------------------------------------------------
// Default (fallback) spec — visual parity with today's MiniappTile
// ---------------------------------------------------------------------------

/** True when `t` satisfies the spec's `text` constraints (1..4 graphemes, ≤64 units). */
function isValidIconText(t: string): boolean {
  if (t.length < 1 || t.length > 64) return false
  const g = graphemeCount(t)
  return g >= 1 && g <= 4
}

/** Uppercase 1–2 char monogram from a display name (schema-valid, guaranteed non-empty). */
function defaultMonogram(stripped: string): string {
  const trimmed = stripped.trim()
  const words = trimmed.split(/\s+/).filter(Boolean)
  let candidate: string
  if (words.length >= 2) {
    // First grapheme of the first two words: "Fox Tracker" → "FT".
    candidate = (graphemes(words[0])[0] ?? '') + (graphemes(words[1])[0] ?? '')
  } else {
    // First two graphemes: "日本語アプリ" → "日本", "x" → "x".
    candidate = graphemes(trimmed).slice(0, 2).join('')
  }
  candidate = candidate.toUpperCase()
  if (isValidIconText(candidate)) return candidate
  // Fall back to a single grapheme, then a stable placeholder — always valid.
  const first = (graphemes(trimmed)[0] ?? '').toUpperCase()
  return isValidIconText(first) ? first : 'A'
}

/**
 * Fallback icon for a mini-app with no stored spec: the PDApp mark on the
 * BRAND gradient — Papan purple (top) into maroon (bottom).
 *
 * FIXED, not name-derived. Until 2026-08-14 the two stops came from a hash of
 * the folder name, so every app got its own hue and a grid of them was
 * distinguishable at a glance. That variety is deliberately traded away: an
 * unconfigured app now reads as *the product's* app rather than as a random
 * colour, and an author who wants a distinct tile sets one in the icon editor.
 *
 * `name` is therefore unused. It is KEPT in the signature because ~10 call
 * sites pass it and a future default may want it again (and because dropping it
 * would silently change nothing at those sites while making the diff larger
 * than the decision).
 *
 * The stops are ordered light→dark top-to-bottom (the gradient is always a
 * vertical 180° ramp), matching the app-icon convention. Swap the two constants
 * to invert it; move `DEFAULT_TILE_GRADIENT_TO_STOP` to change how much of the
 * tile each colour gets (lower ⇒ more of the BOTTOM colour).
 */
/** Top stop — maroon. */
export const DEFAULT_TILE_GRADIENT_FROM = '#800000'
/** Bottom stop. */
export const DEFAULT_TILE_GRADIENT_TO = '#78095f'
/** 1 — the even ramp: each colour gets half the tile, and neither reads as a
 *  band. Lower it to give the BOTTOM colour more of the tile. */
export const DEFAULT_TILE_GRADIENT_TO_STOP = 1

export function defaultIconSpecFor(_name: string): MiniappIconSpec {
  return {
    v: 1,
    icon: MINIAPP_ICON_DEFAULT_LUCIDE,
    emojiStyle: 'color',
    // Default glyph/text color is white (not auto-contrast) — both stops are
    // mid-to-dark, so white reads as the standard "app icon" look.
    textColor: '#ffffff',
    bg: {
      type: 'gradient',
      from: DEFAULT_TILE_GRADIENT_FROM,
      to: DEFAULT_TILE_GRADIENT_TO,
      toStop: DEFAULT_TILE_GRADIENT_TO_STOP,
    },
  }
}


/**
 * A schema-valid up-to-2-char uppercase monogram derived from a mini-app's
 * display name. Used by the editor to seed the Text field when the user
 * switches a default (Lucide) icon into text mode.
 */
export function defaultMonogramFor(name: string): string {
  return defaultMonogram(stripMiniappFolderExt(name))
}

// ---------------------------------------------------------------------------
// Auto text color — WCAG relative luminance
// ---------------------------------------------------------------------------

/** WCAG relative luminance of a `#rrggbb` color. */
function relLuminance(hex: string): number {
  const chan = (i: number) => parseInt(hex.slice(i, i + 2), 16) / 255
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))
  return 0.2126 * lin(chan(1)) + 0.7152 * lin(chan(3)) + 0.0722 * lin(chan(5))
}

/**
 * Pick white or near-black text for a background, by whichever gives the
 * higher WCAG contrast. For gradients, judged against the average of the two
 * stops' luminance.
 */
export function autoTextColor(bg: MiniappIconSpec['bg']): '#ffffff' | '#1c1c1e' {
  // An image background can't be sampled in this DOM-free module — default to
  // white (the standard "app icon" look; the user can override the glyph color).
  if (bg.type === 'image') return '#ffffff'
  const lum =
    bg.type === 'solid'
      ? relLuminance(bg.color)
      : (relLuminance(bg.from) + relLuminance(bg.to)) / 2
  const contrastWhite = 1.05 / (lum + 0.05)
  const contrastBlack = (lum + 0.05) / 0.05
  return contrastBlack >= contrastWhite ? '#1c1c1e' : '#ffffff'
}

// ---------------------------------------------------------------------------
// Stable hash — cache-busting URL segment for server-rendered PNGs
// ---------------------------------------------------------------------------

/** Deterministic JSON string with lexicographically sorted object keys. */
function stableStringify(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'null'
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`
  const obj = v as Record<string, unknown>
  const keys = Object.keys(obj).sort()
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`
}

/**
 * Short, stable, key-order-independent hash of a spec (FNV-1a over the
 * canonicalized JSON), hex-encoded to 8 chars. Pure JS so it runs in the
 * browser too — used as a cache-bust segment in favicon URLs.
 */
export function iconSpecHash(spec: MiniappIconSpec): string {
  return fnv1a(`v${MINIAPP_ICON_RENDER_VERSION}:${stableStringify(spec)}`)
}

/**
 * Bumped whenever the RENDERED PIXELS change for a spec that itself did not.
 *
 * The rev is a pure function of the spec, so a geometry change is invisible to
 * it — and the favicon route serves `immutable`, so a browser holding
 * `icon-<rev>-64.png` would keep the OLD picture for up to a year with nothing
 * to invalidate it. Every stored spec predates `shape`, so without this bump the
 * portrait tile would simply not reach any already-cached icon.
 *
 *   1 — square squircle, lucide `box` default glyph
 *   2 — portrait tile (2026-08-14), PDApp mark default glyph
 */
export const MINIAPP_ICON_RENDER_VERSION = 2

/**
 * Bumped whenever the SERVER rasteriser's pixels change (#1015) — the PNGs the
 * favicon, home-screen icon and link-preview routes serve, and the SPA's
 * `icon.png`. Folded into the URL at the moment it is BUILT (`iconCacheKey`), not
 * into the stored rev: an asset icon's rev is written into `metadata_json` and
 * would otherwise keep its old URL — and its year-long `immutable` cache entry —
 * through any number of renderer fixes.
 *
 *   1 — vendored emoji art + fonts, text drawn as outlines (#1015). Before it,
 *       servers drew emoji blank, letters as boxes and named glyphs as the
 *       default mark, and Cloudflare kept those for a year.
 */
export const ICON_RASTER_VERSION = 1

/**
 * The cache-bust segment every server-rendered icon URL carries — the ONE
 * spelling for every builder (the served app's `<link>`s, the share view's
 * favicon, the SPA's `icon.png?v=`) and every freshness check. `rev` is the
 * stored rev or the spec hash. Hex in, hex out (the routes match `[0-9a-f]+`).
 *
 * A CACHE-BUSTER, NOT AN IDENTITY: it is public (in every served page) and
 * 32-bit, like the rev it folds — a spec hashing to another app's key can be
 * brute-forced in minutes. Nothing may be keyed on it alone: the server's render
 * cache keys on content (`renderKey`, icon-render.ts) and the icon routes forbid
 * shared edge caching (`CDN-Cache-Control: no-store`, preview-serve.ts), because
 * the router's cross-zone URL carries no app token (review, #1015).
 */
export function iconCacheKey(rev: string): string {
  return fnv1a(`raster${ICON_RASTER_VERSION}:${rev}`)
}

/** FNV-1a → 8 hex chars. Shared by the spec hash and the asset rev. */
function fnv1a(str: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(16).padStart(8, '0')
}

/**
 * The single cache-bust `rev` for an icon: the spec hash folded together with the
 * storage `contentHash` of every referenced asset. The metadata-cache writer, the
 * SPA `icon.png` endpoint, and the favicon `respondIcon` MUST all use THIS formula
 * — otherwise their URLs disagree and the `immutable` favicon cache pins a stale
 * render for a year. Falsy asset hashes are skipped (a spec-only icon → just the
 * spec hash). Pure, so the client can validate a rev too.
 */
export function combineIconRev(spec: MiniappIconSpec, assetHashes: Array<string | null | undefined>): string {
  const parts = [iconSpecHash(spec), ...assetHashes.filter((h): h is string => !!h)]
  return fnv1a(parts.join(':'))
}

// ---------------------------------------------------------------------------
// Auto text sizing on the 512 grid
// ---------------------------------------------------------------------------

/**
 * Legacy single-line font-size by grapheme count (1→280 … 4→120). Superseded by
 * `layoutIconText` (which wraps + auto-fits) but kept for callers that only need
 * a rough single-glyph size.
 */
export function iconFontSize(graphemes: number): number {
  if (graphemes <= 1) return 280
  if (graphemes === 2) return 200
  if (graphemes === 3) return 150
  return 120
}

// ---------------------------------------------------------------------------
// Text layout — wrap + auto-fit into the squircle's safe area
// ---------------------------------------------------------------------------
//
// SVG <text> doesn't wrap or auto-size, and neither the browser SVG renderer
// nor server-side librsvg exposes glyph metrics in this shared, DOM-free
// module. So we approximate: model each grapheme as ~AVG_ADVANCE * fontSize
// wide and each line as LINE_HEIGHT * fontSize tall, then binary-search the
// largest font size whose greedy word-wrap fits the safe box within
// MAX_TEXT_LINES. Pure JS + grapheme-safe → the client and the server compute
// IDENTICAL layouts, so a favicon matches the in-app tile.

/** Default safe text box (centered) — the SQUARE tile's. Width < height so a
 *  wide word wraps while stacked lines use the taller vertical budget. The
 *  portrait tile passes its own narrower box via `tileGeometry().text`. */
const TEXT_SAFE_W = 300
const TEXT_SAFE_H = 340
const AVG_ADVANCE = 0.62 // mean glyph advance / fontSize (bold sans, approx)
const LINE_HEIGHT = 1.14 // line box / fontSize
const MAX_TEXT_LINES = 5
const MAX_TEXT_FONT = 280 // single-glyph cap (matches the old 1-char size)

export interface IconTextLayout {
  /** Wrapped lines, top → bottom. */
  lines: string[]
  /** Font size in 512-grid units. */
  fontSize: number
  /** Distance between line centers in 512-grid units. */
  lineHeight: number
}

/** Split a token into ≤maxChars grapheme chunks (for words too long for a line). */
function chunkGraphemes(word: string, maxChars: number): string[] {
  const g = graphemes(word)
  if (g.length <= maxChars) return [word]
  const out: string[] = []
  for (let i = 0; i < g.length; i += maxChars) out.push(g.slice(i, i + maxChars).join(''))
  return out
}

/** Greedy word-wrap so no line exceeds `maxChars` graphemes; long words hard-break. */
function wrapText(text: string, maxChars: number): string[] {
  if (maxChars < 1) return [text]
  const words = text.split(/\s+/).filter(Boolean)
  const lines: string[] = []
  let cur = ''
  for (const w of words) {
    if (graphemeCount(w) > maxChars) {
      if (cur) { lines.push(cur); cur = '' }
      const chunks = chunkGraphemes(w, maxChars)
      for (let i = 0; i < chunks.length - 1; i++) lines.push(chunks[i])
      cur = chunks[chunks.length - 1]
      continue
    }
    const trial = cur ? `${cur} ${w}` : w
    if (graphemeCount(trial) <= maxChars) cur = trial
    else { lines.push(cur); cur = w }
  }
  if (cur) lines.push(cur)
  return lines.length ? lines : ['']
}

/**
 * Wrap `text` into lines and pick the largest font size that fits the squircle
 * safe box (≤ MAX_TEXT_LINES lines). Deterministic and DOM-free so the client
 * SVG and the server PNG lay out identically.
 */
export function layoutIconText(
  text: string,
  safe: { w: number; h: number } = { w: TEXT_SAFE_W, h: TEXT_SAFE_H },
): IconTextLayout {
  const clean = text.trim() || text
  let best: IconTextLayout = { lines: [clean], fontSize: 4, lineHeight: 4 * LINE_HEIGHT }
  let lo = 4
  let hi = MAX_TEXT_FONT
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const maxChars = Math.max(1, Math.floor(safe.w / (mid * AVG_ADVANCE)))
    const lines = wrapText(clean, maxChars)
    const totalH = lines.length * mid * LINE_HEIGHT
    if (lines.length <= MAX_TEXT_LINES && totalH <= safe.h) {
      best = { lines, fontSize: mid, lineHeight: mid * LINE_HEIGHT }
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  return best
}


/**
 * A PORTABLE icon without its two portable-only keys: the resolved `glyph`
 * geometry and the inlined `assets` bytes. ONE list, for every place that turns
 * a portable icon back into a stored spec or hands it to a reader who cannot use
 * those bytes (the markup's metadata, the agent's answers, the maker's seed) — a
 * key added to the portable form is added HERE, or the strict schema rejects the
 * spec and it silently falls back to the default.
 */
export function stripPortableIconKeys<T extends object>(icon: T): Omit<T, 'glyph' | 'assets'> {
  const { glyph: _glyph, assets: _assets, ...rest } = icon as T & { glyph?: unknown; assets?: unknown }
  return rest as Omit<T, 'glyph' | 'assets'>
}
